import { useCallback } from "react";
import { toast } from "react-hot-toast";
import { useSaveBranchFile, useSyncMetadataItem } from "@/hooks/metadata_v1";
import { useMetadataEditorStore, type EditorTab } from "@/stores/metadataEditorStore";
import {
  useKeyboardShortcuts,
  createSaveShortcut,
} from "@/hooks/useKeyboardShortcuts";

interface UseEditorSaveProps {
  useStore: boolean;
  activeTab: EditorTab | undefined;
  activeTabId: string | null;
  /** Set to false to skip keyboard shortcut registration (e.g. when the package handles shortcuts). Defaults to true. */
  registerShortcuts?: boolean;
  /** Callback fired after a successful save — used to trigger post-save analysis (test coverage, impact). */
  onFileSaved?: (data: { id: string; fileName: string; metadataType: string }) => void;
}

interface UseEditorSaveReturn {
  isSaving: boolean;
  isSyncing: boolean;
  handleSave: () => void;
  handleSyncFile: () => Promise<void>;
}

export function useEditorSave({
  useStore,
  activeTab,
  activeTabId,
  registerShortcuts = true,
  onFileSaved,
}: UseEditorSaveProps): UseEditorSaveReturn {
  const updateContent = useMetadataEditorStore((state) => state.updateContent);
  const markSaved = useMetadataEditorStore((state) => state.markSaved);
  const setTabSaving = useMetadataEditorStore((state) => state.setTabSaving);
  const setBranchForTab = useMetadataEditorStore((state) => state.setBranchForTab);

  // Save mutation — creates branch on first save, updates on subsequent saves
  const { mutate: saveFile, isPending: isMutationPending } =
    useSaveBranchFile({
      onSuccess: (branch) => {
        if (activeTabId) {
          // Sync saved branch content + reset dirty state
          markSaved(activeTabId, branch.content ?? activeTab?.content, undefined);
          // Store the branch on the tab (shows branch bar UI)
          setBranchForTab(activeTabId, branch);
        }
        // Trigger post-save analysis (test coverage, impact) if callback provided
        if (onFileSaved && activeTab?.file) {
          const fileName = branch.metadataItem.fileName || activeTab.file.name;
          const metadataType = branch.metadataItem.metadataType || activeTab.file.metadataType;
          if (fileName && metadataType) {
            onFileSaved({ id: activeTab.file.id, fileName, metadataType });
          }
        }
      },
      onError: (error) => {
        if (activeTabId) {
          setTabSaving(activeTabId, false);
        }
        console.error("Failed to save file:", error);
      },
    });

  // Sync from Salesforce mutation
  const { syncItem, isPending: isSyncPending } = useSyncMetadataItem({
    onSuccess: (data) => {
      if (activeTabId) {
        // Update tab content with synced data
        updateContent(activeTabId, data.content);
        // Mark as not dirty since we just synced
        markSaved(activeTabId, data.content, false);
      }
      toast.success("File synced from Salesforce");
    },
  });

  // Sync handler
  const handleSyncFile = useCallback(async () => {
    const isDirty = activeTab?.isDirty ?? false;
    if (!useStore || !activeTab || isDirty || isSyncPending) return;

    try {
      await syncItem({ metadataItemId: activeTab.file.id });
    } catch (error) {
      // Error handling done in hook callbacks
    }
  }, [useStore, activeTab, isSyncPending, syncItem]);

  // Save handler
  const handleSave = useCallback(() => {
    if (!useStore || !activeTab || !activeTab.isDirty) return;
    // Prevent saving while viewing main (read-only mode)
    if (activeTab.viewingMain) return;

    setTabSaving(activeTab.id, true);

    saveFile({
      metadataItemId: activeTab.file.id,
      content: activeTab.content,
      // Only pass name on first save (no existing branch) — auto-generate from file name
      name: activeTab.activeBranch ? undefined : `edit-${activeTab.file.name}`,
    });
  }, [useStore, activeTab, setTabSaving, saveFile]);

  // Register keyboard shortcuts (skip when the package editor handles them)
  useKeyboardShortcuts({
    shortcuts: [
      createSaveShortcut(handleSave, useStore && !!activeTab?.isDirty),
    ],
    enabled: useStore && registerShortcuts,
  });

  const isSaving = activeTab?.isSaving ?? isMutationPending;

  return {
    isSaving,
    isSyncing: isSyncPending,
    handleSave,
    handleSyncFile,
  };
}
